import React, { useRef } from "react";

const OtpStep = ({
  phone,
  otp,
  setOtp,
  otpResendTimer,
  loadingAction,
  onVerifyOtp,
  onResendOtp,
  onBack,
}) => {

  const inputRefs = useRef([]);

  // Focus next input
  const focusInput = (index) => {
    setTimeout(() => {
      inputRefs.current[index]?.focus();
      inputRefs.current[index]?.select();
    }, 0);
  };

  // ─── OTP Input Handlers (with paste & backspace support) ───
  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, ""); // only digits

    const newOtp = [...otp];

    // If user deleted (backspace on mobile numeric pad)
    if (value === "" && otp[index] !== "") {
      newOtp[index] = "";
      setOtp(newOtp);

      // Move to previous if possible
      if (index > 0) {
        focusInput(index - 1);
      }
      return;
    }

    // Normal digit input or paste
    if (value.length > 0) {
      // Handle paste (multiple digits)
      if (value.length > 1) {
        for (let i = 0; i < value.length && index + i < 6; i++) {
          newOtp[index + i] = value[i];
        }
        setOtp(newOtp);
        const next = Math.min(index + value.length, 5);
        focusInput(next);
        return;
      }

      // Single digit
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < 5) {
        focusInput(index + 1);
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();

      const newOtp = [...otp];

      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        focusInput(index - 1);
      }
    }
  };

  const filledCount = otp.filter(Boolean).length;

  return (
    <div className="flex flex-col h-full flex-1 pt-5 sm:pt-0 justify-between gap-5">
      <div className="space-y-3">
        {/* Info banner */}
        <div className="flex items-start gap-3 bg-primaryColor/5 border border-primaryColor/15 rounded-xl px-4 py-3">
          <div className="w-8 h-8 rounded-full bg-primaryColor/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-primaryColor" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700">OTP sent to your mobile</p>
            <p className="text-xs text-gray-500 mt-0.5">
              +91 <span className="font-semibold text-gray-700 tracking-wider">{phone}</span>
            </p>
          </div>
        </div>

        {/* 6-box OTP input */}
        <div className="flex justify-between gap-1.5">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(e, index)}
              onKeyDown={(e) => handleOtpKeyDown(e, index)}
              onFocus={(e) => e.target.select()}
              onPaste={(e) => {
                e.preventDefault();
                handleOtpChange(
                  { target: { value: e.clipboardData.getData("Text") } },
                  index
                );
              }}
              className={`
               flex-1 aspect-square max-w-[42px] text-center border-2 rounded-lg
    text-base font-bold outline-none transition-all duration-150
                ${digit
                  ? "border-primaryColor bg-primaryColor/5 text-primaryColor"
                  : "border-gray-200 bg-white text-gray-800 focus:border-primaryColor focus:bg-primaryColor/5"
                }
              `}
            />
          ))}
        </div>

        {/* Progress indicator */}
        <div className="flex gap-1.5">
          {otp.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all duration-300 ${i < filledCount ? "bg-primaryColor" : "bg-gray-200"}`}
            />
          ))}
        </div>

        {/* Resend OTP */}
        <div className="flex items-center justify-center gap-1.5">
          <p className="text-xs text-gray-500">Didn't receive the OTP?</p>
          <button
            type="button"
            onClick={onResendOtp}
            disabled={otpResendTimer > 0}
            className={`
            text-xs font-semibold transition-all duration-200
            ${otpResendTimer > 0
                ? "text-gray-400 cursor-not-allowed"
                : "text-primaryColor hover:underline underline-offset-2"
              }
          `}
          >
            {otpResendTimer > 0 ? `Resend in ${otpResendTimer}s` : "Resend OTP"}
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Back – lets the user edit their phone number */}
        <button
          type="button"
          onClick={onBack}
          className="
            flex-1 border-2 border-gray-200 text-gray-600 py-3.5 rounded-xl
            font-medium text-sm hover:border-primaryColor hover:text-primaryColor
            transition-all duration-200 active:scale-[0.98]
            flex items-center justify-center gap-2
          "
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5 5-5M18 12H6" />
          </svg>
          Edit Number
        </button>

        <button
          onClick={onVerifyOtp}
          disabled={loadingAction === "verify-otp" || otp.join("").length < 6}
          className="
            flex-1 bg-primaryColor text-white py-3.5 rounded-xl
            font-semibold text-sm hover:bg-primaryColor/90
            transition-all duration-200 active:scale-[0.98]
            disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100
            flex items-center justify-center gap-2 shadow-md shadow-primaryColor/30
          "
        >
          {loadingAction === "verify-otp" ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Verifying...
            </>
          ) : (
            <>
              Verify OTP
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OtpStep;